import { BigNumber } from '@0x/utils'
import assetUtils, { Asset } from 'app-lib/assetUtils'
import loanUtils from 'app-lib/loanUtils'
import { autoFormat, formatPercent, formatPrecision } from 'app-lib/numberUtils'
import providerUtils from 'app-lib/providerUtils'
import appConfig from 'bzx-common/src/config/appConfig'
import BorrowRequest from 'bzx-common/src/domain/borrow/BorrowRequest'
import IBorrowedFundsState from 'bzx-common/src/domain/IBorrowedFundsState'
import ExpectedResult from 'bzx-common/src/shared-components/ExpectedResult'
import { RootStore } from 'bzx-common/src/stores'
import _ from 'lodash'
import React, { Component, FormEvent } from 'react'
import { InputAmount } from 'shared-components/InputAmount/InputAmount'
import { Button, Form, Icon, InfoTip } from 'ui-framework'
import fulcrumProvider from '../../../services/FulcrumProvider'

export interface IBorrowMoreFormProps {
  loan: IBorrowedFundsState
  rootStore: RootStore
  onSubmit: (value: BorrowRequest) => void
  onCancel: () => void
}

interface IBorrowMoreFormState {
  borrowAmount: BigNumber
  inputAmount: string
  borrowMoreCollaterizationMin: BigNumber
  activeToken: Asset
  maxBorrow: BigNumber
  maxAvailableLiquidity: BigNumber
  assetBalance: BigNumber
  isLoading: boolean
  collateralizedPercent: BigNumber
  liquidationPrice: BigNumber
  nextBorrowInterestRate: BigNumber | null
  collateralToLoanRate: BigNumber
}

export default class BorrowMoreForm extends Component<IBorrowMoreFormProps, IBorrowMoreFormState> {
  private _onInputUpdate = _.debounce((value) => this.getExpectedData(value), appConfig.delay)
  public _borrowDecimals = assetUtils.getDecimals(
    fulcrumProvider.currentChainId,
    this.props.loan.loanAsset
  )
  public loanCollateralizedPercent = this.props.loan.collateralizedPercent.times(100).plus(100)

  constructor(props: IBorrowMoreFormProps) {
    super(props)

    this.state = {
      borrowAmount: new BigNumber(0),
      borrowMoreCollaterizationMin: new BigNumber(150),
      inputAmount: '',
      activeToken: assetUtils.getDefaultActiveToken(
        fulcrumProvider.currentChainId,
        this.props.loan.loanAsset,
        this.props.loan.collateralAsset
      ),
      maxAvailableLiquidity: new BigNumber(0),
      maxBorrow: new BigNumber(0),
      assetBalance: new BigNumber(0),
      isLoading: true,
      collateralizedPercent: this.loanCollateralizedPercent,
      liquidationPrice: new BigNumber(0),
      nextBorrowInterestRate: null,
      collateralToLoanRate: new BigNumber(NaN),
    }
  }

  public async componentDidMount() {
    const { amountOwed, loanAsset, loanData, collateralAmount, collateralAsset } = this.props.loan
    const minInitialMargin = (
      await loanUtils.getLoanParams(fulcrumProvider, loanAsset, collateralAsset, true)
    ).minInitialMargin
      .div(10 ** 18)
      .plus(100)

    const borrowMoreCollaterizationMin = minInitialMargin.plus(30)
    const collateralizationTooLow =
      !this.state.collateralizedPercent.isZero() &&
      this.state.collateralizedPercent.lte(this.state.borrowMoreCollaterizationMin)

    const assetBalance = await providerUtils.getTokenBalanceOfUser(fulcrumProvider, loanAsset)

    const borrowAmountEstimate = await loanUtils.getBorrowAmountEstimate(
      fulcrumProvider,
      loanAsset,
      collateralAsset,
      collateralAmount,
      borrowMoreCollaterizationMin
    )

    const { loanService } = this.props.rootStore.services
    const liquidity = await loanService.getLiquidityFromBlockchain([loanAsset])
    const maxAvailableLiquidity = new BigNumber(liquidity[loanAsset.toLowerCase()]).times(0.95)

    // get maxBorrow from amountOwed
    // if estimated borrowAmount for min collateralization level is less than amountOwed
    const maxBorrow = !collateralizationTooLow
      ? borrowAmountEstimate.borrowAmount.gt(amountOwed)
        ? borrowAmountEstimate.borrowAmount.minus(amountOwed)
        : amountOwed.times(0.1)
      : new BigNumber(0)

    const activeToken = assetUtils.getDefaultActiveToken(
      fulcrumProvider.currentChainId,
      loanAsset,
      collateralAsset
    )

    const liquidationPrice = loanUtils.getLiquidationPrice(
      fulcrumProvider,
      this.props.loan,
      loanData.collateral,
      activeToken
    )

    this.setState(
      {
        activeToken,
        assetBalance,
        borrowMoreCollaterizationMin,
        collateralToLoanRate: borrowAmountEstimate.collateralToLoanRate,
        isLoading: false,
        maxAvailableLiquidity,
        maxBorrow,
        liquidationPrice,
      },
      () => this.setAmountFromPercentage(1)
    )
  }

  public render() {
    const loan = this.props.loan
    const { spriteLogo } = assetUtils.getCommon(loan.loanAsset)
    const { maxBorrow, nextBorrowInterestRate } = this.state

    let sliderValue = this.state.borrowAmount.div(this.state.maxBorrow).toNumber()
    if (sliderValue > 1) {
      sliderValue = 1
    }

    const collateralizationTooLow =
      !this.state.collateralizedPercent.isZero() &&
      this.state.collateralizedPercent.lte(this.state.borrowMoreCollaterizationMin)

    const liquidationPrice = collateralizationTooLow
      ? new BigNumber(NaN)
      : this.state.liquidationPrice

    const loanStatus = collateralizationTooLow ? 'risky' : 'safe'
    const submitTitle = this.state.borrowAmount.toFixed(this._borrowDecimals, 1)
    const warningMessage = collateralizationTooLow ? (
      <>
        A minimum of&nbsp;<b>{this.state.borrowMoreCollaterizationMin.toFixed(2)}</b>%
        collaterilization is required
      </>
    ) : this.state.borrowAmount.lt(0) ? (
      `Please enter a positive number`
    ) : this.state.borrowAmount.gt(this.state.maxAvailableLiquidity) ? (
      'There is insufficient liquidity available for this loan'
    ) : undefined

    const canSubmit = !warningMessage && this.state.borrowAmount.gt(0)
    return (
      <Form onCancel={this.props.onCancel} onSubmit={this.onSubmit}>
        <section className="form__left_block">
          <div className="form__info_block">
            <div className="form__info_block__logo">
              <Icon name={spriteLogo} size="auto" className="form__info_block__logo_svg" />
            </div>
            <div className="flex-col width-100p">
              <div className="txt-title txt-default form--asset">Borrow More</div>
              <div className="borrow-flex--column-row">
                <div className="flex-row-center justify-between">
                  <div className="flex-col margin-top-1">
                    <div className="txt-alternate txt-muted margin-right-1ch">APR</div>
                    <div className="txt-default txt-subtitle">
                      <span title={loan.interestRate.toFixed()}>
                        {formatPercent(loan.interestRate)}%
                      </span>{' '}
                      {nextBorrowInterestRate && (
                        <span className="txt-positive" title={nextBorrowInterestRate.toFixed()}>
                          → {formatPercent(nextBorrowInterestRate)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <InfoTip id="borrow-apr-tooltip" top={-3}>
                    <div className="txt-explanation">
                      <p>This is the % of interest paid per year on your position.</p>
                      {nextBorrowInterestRate && nextBorrowInterestRate.gte(0.001) && (
                        <p>
                          <b>
                            With you current setup, the interest rates will raise to{' '}
                            {formatPercent(nextBorrowInterestRate)}% approximately.
                          </b>
                        </p>
                      )}
                      <p>
                        The rates are dynamic and depend on the pool utilization. Thanks to Ooki,
                        you will benefit from very low rates (almost free money) until the pool
                        reaches 80% of utilization.
                      </p>
                    </div>
                  </InfoTip>
                </div>
              </div>
              {!this.state.collateralToLoanRate.isNaN() && (
                <div className="margin-top-2">
                  <div className="txt-alternate txt-muted">Price of 1 {loan.collateralAsset}</div>
                  <div className="txt-subtitle">
                    {autoFormat(this.state.collateralToLoanRate)} {loan.loanAsset}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="form__content">
          <div className="flex-row flex-between margin-bottom-05">
            <div className="flex-column">
              <div className="txt-subtitle margin-bottom-05">Your Collateral</div>
              <div className="txt-title margin-bottom-1">
                {autoFormat(loan.collateralAmount)} {loan.collateralAsset}
              </div>
            </div>
            <div className="flex-column txt-right">
              <div className="txt-subtitle margin-bottom-05">Currently Borrowed</div>
              <div title={`${loan.amountOwed.toFixed()} ${loan.loanAsset}`} className="txt-title">
                {autoFormat(loan.amountOwed)} <span>{loan.loanAsset}</span>
              </div>
            </div>
          </div>

          <div className="margin-bottom-1">
            <InputAmount
              isLoading={this.state.isLoading}
              inputAmount={this.state.inputAmount}
              selectedAsset={loan.loanAsset}
              buttonValue={sliderValue}
              onInsertMaxValue={this.setAmountFromPercentage}
              onAmountChange={this.onTradeAmountChange}
              onAfterSliderChange={() => this._onInputUpdate(this.state.inputAmount)}
              withSlider
              isGrouped>
              <div className="input-amount__row-container">
                <div className="txt-subtitle">You will Borrow More</div>
                <div
                  className="txt-subtitle txt-default"
                  title={maxBorrow.toFixed() + ' ' + loan.loanAsset}>
                  <Button
                    variant="link"
                    value={1}
                    onClickEmit={'value'}
                    onClick={this.setAmountFromPercentage}>
                    {autoFormat(maxBorrow)}
                  </Button>{' '}
                  <span className="margin-right-1ch">{loan.loanAsset} Max</span>
                  <InfoTip id="borrow-more-max">
                    This is the maximum amount of {loan.loanAsset} that you can borrow with your
                    current collateral and the liquidity available.{' '}
                  </InfoTip>
                </div>
              </div>
            </InputAmount>
          </div>

          <div className="margin-v-1">
            <ExpectedResult
              collaterizationPercents={this.state.collateralizedPercent}
              liquidationPrice={liquidationPrice}
              loanToken={loan.loanAsset}
              collateralToken={loan.collateralAsset}
              loanStatus={loanStatus}
              chainId={fulcrumProvider.currentChainId}
            />
          </div>

          {warningMessage && !this.state.isLoading ? (
            <p className="form__warning">{warningMessage}</p>
          ) : (
            <Button
              type="submit"
              variant="green"
              isLoading={this.state.isLoading}
              titile={submitTitle}
              disabled={!canSubmit}>
              Borrow {autoFormat(this.state.borrowAmount)} {loan.loanAsset}
            </Button>
          )}
        </section>
      </Form>
    )
  }

  public onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const { loanId, loanAsset, collateralAsset } = this.props.loan

    this.props.onSubmit(
      new BorrowRequest(
        loanId,
        loanAsset,
        this.state.borrowAmount,
        collateralAsset,
        new BigNumber(0)
      )
    )
  }

  public onTradeAmountChange = async (inputAmount: string) => {
    this.setState(
      {
        inputAmount,
        borrowAmount: new BigNumber(inputAmount),
      },
      () => {
        this._onInputUpdate(inputAmount)
      }
    )
  }

  /**
   * @param percentage from 0 to 1
   */
  public setAmountFromPercentage = (percentage: number) => {
    const borrowAmount = this.state.maxBorrow.times(percentage)
    this.setState(
      {
        borrowAmount,
        inputAmount: formatPrecision(borrowAmount),
      },
      () => this._onInputUpdate(this.state.inputAmount)
    )
  }

  async refreshBorrowRate(borrowAmount: BigNumber) {
    try {
      const nextBorrowInterestRate = await loanUtils.nextBorrowInterestRate(
        fulcrumProvider,
        this.props.loan.loanAsset,
        borrowAmount
      )
      this.setState({ nextBorrowInterestRate })
    } catch (err) {
      // fail silently for now; TODO: discuss how to handle error on the UI
      console.error(err)
    }
  }

  private getExpectedData = async (inputAmount: string) => {
    const amountBN = new BigNumber(inputAmount)
    if (amountBN.isNaN() || !amountBN.gt(0)) {
      return
    }
    const result = await loanUtils.getBorrowMoreChangeEstimate(
      fulcrumProvider,
      this.props.loan,
      new BigNumber(inputAmount),
      this.state.activeToken
    )

    this.setState({
      collateralizedPercent: result.collateralizedPercent,
      liquidationPrice: result.liquidationPrice,
    })

    this.refreshBorrowRate(amountBN)
  }
}
